export interface GroupSession {
  scheduledAt: string;
  cancelledAt: string | null;
}

export type GroupTherapyState = "open" | "locked" | "completed" | "cancelled";

export function firstSessionAt(sessions: GroupSession[]): string | null {
  const active = sessions.filter((s) => !s.cancelledAt);
  const first = active[0];
  if (!first) return null;
  return active.reduce((min, s) => (s.scheduledAt < min ? s.scheduledAt : min), first.scheduledAt);
}

export function lastSessionAt(sessions: GroupSession[]): string | null {
  const active = sessions.filter((s) => !s.cancelledAt);
  const first = active[0];
  if (!first) return null;
  return active.reduce((max, s) => (s.scheduledAt > max ? s.scheduledAt : max), first.scheduledAt);
}

export function deriveGroupTherapyState(
  sessions: GroupSession[],
  cancelledAt: string | null,
  now: Date = new Date()
): GroupTherapyState {
  if (cancelledAt) return "cancelled";
  const nowMs = now.getTime();
  const last = lastSessionAt(sessions);
  if (last && Date.parse(last) <= nowMs) return "completed";
  const first = firstSessionAt(sessions);
  if (first && Date.parse(first) <= nowMs) return "locked";
  return "open";
}

export function isLocked(
  sessions: GroupSession[],
  cancelledAt: string | null,
  now: Date = new Date()
): boolean {
  return deriveGroupTherapyState(sessions, cancelledAt, now) !== "open";
}

export function remainingSeats(
  maxParticipants: number,
  activeEnrollmentCount: number
): number {
  return Math.max(0, maxParticipants - activeEnrollmentCount);
}

/**
 * True when two schedules hold the same instants, regardless of order or ISO
 * spelling ("...T10:00Z" vs "...T10:00:00.000Z"). Lets an edit of name or
 * description re-send an unchanged schedule without counting as a schedule change.
 */
export function sameSessionSchedule(a: readonly string[], b: readonly string[]): boolean {
  if (a.length !== b.length) return false;
  const norm = (list: readonly string[]) => list.map((s) => Date.parse(s)).sort((x, y) => x - y);
  const na = norm(a);
  const nb = norm(b);
  return na.every((v, i) => !Number.isNaN(v) && v === nb[i]);
}
